import { Box, DialogContent, styled, useTheme } from '@mui/material';
import useDialog from 'hooks/useDialog';
import { DialogDefault } from 'DesignSystem';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { IDefaultDialogProps } from 'types/dialog.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { IBaseClusterSite } from 'FeatureModules/Site/types/site.types';
import FlexBox from 'Components/FlexBox';
import SeparatorLine from 'Components/SeparatorLine';

interface IAddMangeClusterDecisionProps extends IDefaultDialogProps {
  siteCluster: IBaseClusterSite;
  partner?: IBasePartnerCompany;
}

const ContentWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
}));

const AddMangeClusterDecisionDialog: FC<IAddMangeClusterDecisionProps> = ({
  onClose,
  siteCluster,
  partner,
}) => {
  const theme = useTheme();
  const { openDialog } = useDialog();
  const navigate = useNavigate();

  const handleImportFarms = () => {
    navigate(`/imports?new=1&clusterId=${siteCluster.id}`);
    onClose?.();
  };

  const handleCreateSite = () => {
    openDialog({
      type: 'ADD_EDIT_SITE',
      props: { siteClusterId: siteCluster.id, ownedBy: partner },
    });
    onClose?.();
  };

  const handleSelectSites = () => {
    openDialog({ type: 'MANAGE_CLUSTER_SITES', props: { siteCluster, partner } });
    onClose?.();
  };

  return (
    <DialogDefault
      title="Manage cluster sites"
      iconName="site-cluster-manage"
      onClose={onClose}
      open
      fullWidth
      maxWidth="sm"
      background="#fff"
      preventCloseOnBackground
    >
      <DialogContent>
        <ContentWrapper>
          <ThemeButton
            color="PRIMARY"
            fullWidth
            startIcon={<Icon name="import" color="WHITE" />}
            size="large"
            onClick={handleImportFarms}
          >
            Import farms
          </ThemeButton>

          <Box mt={1}>
            <ThemeTypography variant="BODY_SMALL" color="GRAY" textAlign="center">
              Import farm data in bulk. Advanced geo-data file import available.
            </ThemeTypography>
          </Box>

          <SeparatorLine label="or" width="80%" backgroundColor={theme.custom.colors.offWhite} />

          <FlexBox gap={3} width="100%">
            <Box width="50%">
              <ThemeButton
                color="BLUE_ICE"
                fullWidth
                size="large"
                startIcon={<Icon name="site-cluster-manage" />}
                onClick={handleSelectSites}
              >
                Manage sites in cluster
              </ThemeButton>

              <Box mt={1} textAlign="center">
                <ThemeTypography variant="BODY_SMALL" color="GRAY" mb={2}>
                  Add or remove existing sites.
                </ThemeTypography>
              </Box>
            </Box>

            <Box width="50%">
              <ThemeButton
                color="BLUE_ICE"
                fullWidth
                size="large"
                startIcon={<Icon name="plus" />}
                onClick={handleCreateSite}
              >
                Add new site
              </ThemeButton>

              <Box mt={1} textAlign="center">
                <ThemeTypography variant="BODY_SMALL" color="GRAY" mb={2}>
                  Add a new site to this cluster manually.
                </ThemeTypography>
              </Box>
            </Box>
          </FlexBox>
        </ContentWrapper>
      </DialogContent>
    </DialogDefault>
  );
};

export default AddMangeClusterDecisionDialog;
